'use client';

import { useEffect } from 'react';
import { CONSENT_COOKIE } from '@/lib/consent';

/**
 * Mounts @beyonk/gdpr-cookie-consent-banner.
 *
 * The package's entry point is a raw .svelte file, which a React app cannot
 * import — but it also ships a prebuilt bundle that registers the
 * <cookie-consent-banner> custom element, which is framework-agnostic. That
 * bundle is copied into public/vendor at build time (scripts/copy-vendor-assets.mjs)
 * and loaded here.
 *
 * The element is created imperatively rather than rendered as JSX: it only exists
 * on the client, so there is no server markup for React to reconcile against and
 * no hydration mismatch to manage.
 */

const SCRIPT_SRC = '/vendor/gdpr-banner.js';

// Only "necessary" and "analytics" apply here. The library hides a category when
// it is set to false, so PitchVault never shows advertising categories it does
// not use.
//
// `value: true` on analytics controls the checkbox inside the preferences panel,
// which is what makes the primary button read "Accept all" instead of silently
// saving a rejection. Consent still comes from the visitor's click: nothing is
// stored and hasAnalyticsConsent() stays false until they choose, and "Reject
// analytics" sits alongside with equal weight.
const CHOICES = {
  necessary: {
    label: 'Essential',
    description:
      'Keeps you signed in, and lets us deliver a document to the browser that was granted access. Cannot be turned off.',
    value: true,
  },
  analytics: {
    label: 'Read analytics',
    description:
      'Lets the person who shared this document see which pages you read and for how long. Turning this off stops that measurement.',
    value: true,
  },
  tracking: false,
  marketing: false,
};

export function CookieConsent() {
  useEffect(() => {
    let cancelled = false;

    function mount() {
      if (cancelled || document.querySelector('cookie-consent-banner')) return;
      const el = document.createElement('cookie-consent-banner');
      el.setAttribute('cookie-name', CONSENT_COOKIE);
      el.setAttribute('can-reject-cookies', 'true');
      el.setAttribute('show-edit-icon', 'true');
      el.setAttribute('heading', 'Your privacy');
      el.setAttribute(
        'description',
        'PitchVault needs a couple of essential cookies to sign you in and to deliver documents. ' +
          'Separately, the sender can measure how long you spend on each page — that is off unless you allow it. ' +
          'See our <a href="/privacy">privacy notice</a>.',
      );
      el.setAttribute('accept-all-label', 'Accept all');
      el.setAttribute('accept-selected-label', 'Accept selected');
      el.setAttribute('reject-label', 'Reject analytics');
      el.setAttribute('settings-label', 'Choose');
      el.setAttribute('choices', JSON.stringify(CHOICES));
      el.setAttribute('cookie-config', JSON.stringify({ path: '/', sameSite: 'lax' }));
      document.body.appendChild(el);
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') mount();
      else existing.addEventListener('load', mount, { once: true });
    } else {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.type = 'module';
      script.async = true;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        mount();
      });
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return <link rel="stylesheet" href="/vendor/gdpr-banner.css" />;
}
